<template>
  <div class="file-uploader-container">
    <div :class="uploaderClasses" @click="triggerFileInput" @dragover.prevent @dragenter.prevent @drop.prevent="handleDrop">
      <!-- Title section with icon and text - Always present -->
      <div class="file-uploader__title flex items-center justify-center gap-2">
        <div class="file-uploader__icon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="primary-color">
            <path
              d="M8 1L8 11M8 1L5 4M8 1L11 4M2 11L2 13C2 14.1046 2.89543 15 4 15L12 15C13.1046 15 14 14.1046 14 13L14 11"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <!-- todo -->
        <span class="file-uploader__text">Click to upload a file</span>
      </div>

      <!-- Size limit / Progress text - Always present -->
      <div class="file-uploader__size-limit">
        {{ bottomText }}
      </div>

      <app-loader v-if="isUploading" color="var(--primary-color)" />

      <!-- File section - Only shown when file is uploaded -->
      <div v-if="hasUploadedFile" class="file-uploader__file-section">
        <div class="file-uploader__file-info">
          <!-- File Icon -->
          <div class="file-uploader__file-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.47696 1.23172C9.11926 -0.410575 11.7843 -0.410575 13.4266 1.23172C15.0689 2.87402 15.0689 5.53911 13.4266 7.18141L8.63416 11.9739C7.62891 12.9791 6.00123 12.9791 4.99597 11.9739C3.99072 10.9686 3.99072 9.34094 4.99597 8.33569L9.46116 3.87635C9.82644 3.51107 10.4197 3.51107 10.7849 3.87635C11.1502 4.24163 11.1502 4.83485 10.7849 5.20013L6.31975 9.66239C6.04506 9.93708 6.04506 10.3813 6.31975 10.653C6.59444 10.9248 7.03862 10.9277 7.31039 10.653L12.1029 5.86055C13.0146 4.94882 13.0146 3.46724 12.1029 2.5555C11.1911 1.64376 9.70955 1.64376 8.79781 2.5555L4.00533 7.34798C2.45362 8.89969 2.45362 11.4157 4.00533 12.9674C5.55704 14.5192 8.07309 14.5192 9.6248 12.9674L13.4296 9.16561C13.7948 8.80033 14.3881 8.80033 14.7533 9.16561C15.1186 9.53089 15.1186 10.1241 14.7533 10.4894L10.9486 14.2883C8.66631 16.5706 4.96675 16.5706 2.68448 14.2883C0.402211 12.006 0.402211 8.30647 2.68448 6.0242L7.47696 1.23172Z"
                fill="#B1B1BF"
              />
            </svg>
          </div>

          <!-- File Details -->
          <div class="file-uploader__file-details">
            <div class="file-uploader__file-name text-black-01">{{ displayFileName }}</div>
          </div>
        </div>

        <!-- Remove Button -->
        <button class="file-uploader__remove-btn" @click.stop="removeFile" type="button">
          <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#B1B1BF" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" class="hidden" :accept="acceptedFileTypes" @change="handleFileSelect" />
  </div>
</template>

<script>
import request from '@/services/request';
import AppLoader from '@/common/AppLoader.vue';
import { mapState } from 'vuex';

export default {
  name: 'FileUploader',
  components: {
    AppLoader
  },
  props: {
    acceptedFileTypes: {
      type: String,
      default: '*'
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    },
    dirty: {
      type: Boolean,
      default: false
    },
    uploadedFileName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      hasError: false,
      errorMessage: 'File too large',
      selectedFile: null,
      isUploading: false,
      uploadingFile: null
    };
  },
  computed: {
    ...mapState(['orgId']),

    uploaderClasses() {
      return [
        'file-uploader',
        {
          'file-uploader--error': this.hasError,
          'file-uploader--uploading': this.isUploading,
          'file-uploader--uploaded': this.hasUploadedFile && !this.isUploading,
          'file-uploader--dirty': this.dirty
        }
      ];
    },

    hasUploadedFile() {
      return this.selectedFile || this.uploadedFileName;
    },

    displayFileName() {
      if (this.uploadedFileName) {
        return this.uploadedFileName;
      }

      return 'Unknown';
    },

    bottomText() {
      if (this.hasError) {
        return this.errorMessage;
      }
      return 'Up to 10 MB';
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndProcessFile(file);
      }
    },

    handleDrop(event) {
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndProcessFile(files[0]);
      }
    },

    validateAndProcessFile(file) {
      this.hasError = false;

      if (file.size > this.maxFileSize) {
        this.hasError = true;
        this.errorMessage = 'File too large';
        return;
      }

      // File is valid - start upload immediately
      this.uploadFile(file);
    },

    async uploadFile(file) {
      this.isUploading = true;
      this.uploadingFile = file;
      this.hasError = false;

      try {
        const formData = new FormData();
        formData.append('file', file);

        const { data } = await request.fileUpload('post', `/public/file-upload/${this.orgId}`, formData);

        // Upload successful
        this.selectedFile = file;
        this.uploadingFile = null;
        this.isUploading = false;

        this.$emit('file-uploaded', data);
      } catch (error) {
        console.error('File upload error:', error);
        this.hasError = true;
        this.errorMessage = 'Upload failed. Please try again.';
        this.isUploading = false;
        this.uploadingFile = null;

        this.$emit('upload-error', error);
      }
    },

    clearError() {
      this.hasError = false;
    },

    removeFile() {
      this.selectedFile = null;
      this.hasError = false;
      this.isUploading = false;
      this.uploadingFile = null;
      this.$refs.fileInput.value = '';
      this.$emit('file-removed');
    }
  }
};
</script>
